/**
 * Spam-Pre-Flight-Check (browser- & server-safe).
 *
 * Analysiert Betreffzeile und HTML-Rumpf vor dem Versand auf typische
 * Spam-Signalwörter, Ausrufezeichen, Großschreibung und fehlende Pflichtlinks.
 */
const GERMAN_SPAM_WORDS = [
    'gratis',
    '100% kostenlos',
    'kostenfrei',
    'sofort geld',
    'reich werden',
    'jetzt zuschlagen',
    'garantiert',
    'risikofrei',
    'kein risiko',
    'kredit ohne schufa',
    'gewinnen',
    'gewinnspiel',
    'nur heute',
];
const ENGLISH_SPAM_WORDS = [
    'free money',
    '100% free',
    'act now',
    'risk free',
    'no risk',
    'click here',
    'buy now',
    'earn $',
    'make money',
    'guaranteed',
    'winner',
];
export function checkSpamScore(subject, html) {
    const issues = [];
    let score = 0;
    const cleanSubject = subject.trim();
    const lowerSubject = cleanSubject.toLowerCase();
    const lowerHtml = html.toLowerCase();
    const plainText = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    // 1. Betreff fehlt oder zu kurz
    if (cleanSubject.length === 0) {
        issues.push({
            rule: 'subject_empty',
            penalty: 40,
            message: 'Der Betreff ist leer.',
        });
        score += 40;
    }
    else if (cleanSubject.length < 5) {
        issues.push({
            rule: 'subject_short',
            penalty: 15,
            message: 'Der Betreff ist sehr kurz (unter 5 Zeichen).',
        });
        score += 15;
    }
    // 2. Exzessive Großschreibung im Betreff
    if (cleanSubject.length > 5 && cleanSubject === cleanSubject.toUpperCase()) {
        issues.push({
            rule: 'subject_all_caps',
            penalty: 25,
            message: 'Der Betreff besteht nur aus GROSSBUCHSTABEN (ALL CAPS).',
        });
        score += 25;
    }
    // 3. Exzessive Ausrufezeichen oder Fragezeichen
    if (/[!?!]{2,}/.test(cleanSubject)) {
        issues.push({
            rule: 'excessive_punctuation',
            penalty: 15,
            message: 'Der Betreff enthält mehrfache Ausrufe- oder Fragezeichen (!!, ??).',
        });
        score += 15;
    }
    // 4. Spam-Signalwörter im Betreff
    for (const word of [...GERMAN_SPAM_WORDS, ...ENGLISH_SPAM_WORDS]) {
        if (lowerSubject.includes(word)) {
            issues.push({
                rule: 'spam_word_subject',
                penalty: 20,
                message: `Signalwort im Betreff gefunden: "${word}".`,
            });
            score += 20;
            break;
        }
    }
    // 5. Spam-Signalwörter im Inhalt
    let contentSpamHits = 0;
    for (const word of [...GERMAN_SPAM_WORDS, ...ENGLISH_SPAM_WORDS]) {
        if (lowerHtml.includes(word)) {
            contentSpamHits++;
        }
    }
    if (contentSpamHits > 0) {
        const penalty = Math.min(30, contentSpamHits * 10);
        issues.push({
            rule: 'spam_words_body',
            penalty,
            message: `Ver verdächtige Signalwörter im Text gefunden (${contentSpamHits} Treffer).`,
        });
        score += penalty;
    }
    // 6. Abmeldelink fehlt
    const hasUnsubLink = lowerHtml.includes('{{abmelde_link}}') ||
        lowerHtml.includes('{{abmeldelink}}') ||
        lowerHtml.includes('{{abmelde_url}}') ||
        lowerHtml.includes('{{abmelden}}') ||
        lowerHtml.includes('{{unsubscribe_link}}') ||
        lowerHtml.includes('{{unsubscribe_url}}') ||
        lowerHtml.includes('unsubscribe') ||
        lowerHtml.includes('abmelden');
    if (!hasUnsubLink) {
        issues.push({
            rule: 'missing_unsubscribe',
            penalty: 30,
            message: 'Kein Abmelde-Link ({{abmelde_link}}) im Inhalt gefunden.',
        });
        score += 30;
    }
    // 7. Sehr wenig Text
    if (plainText.length < 30) {
        issues.push({
            rule: 'too_little_text',
            penalty: 15,
            message: 'Sehr wenig Text im Inhalt (unter 30 Zeichen).',
        });
        score += 15;
    }
    // 8. Beispiel-Domain (example.com) löst SpamAssassin Razor2 Sperren aus
    if (lowerHtml.includes('example.com')) {
        issues.push({
            rule: 'razor2_example_domain',
            penalty: 35,
            message: 'Enthält „example.com“-Beispielslinks. Ersetze sie durch deine echte Website-Domain (Razor2-Spamfilter).',
        });
        score += 35;
    }
    // 9. Unvollständige Platzhalter (z. B. "XX XXX XX", "000000")
    if (/(?:xx\s*xx|\+?\d+\s*\(0\)\s*x{2,}|000000)/i.test(plainText)) {
        issues.push({
            rule: 'placeholder_values',
            penalty: 25,
            message: 'Enthält unvollständige Platzhalter (z. B. „XX XXX“ bei Telefonnummern). Spam-Systeme wie Razor2 stufen Platzhalter sofort als Spam ein.',
        });
        score += 25;
    }
    // 10. Ungültige HTML-Struktur (Inhalt nach </html>)
    if (/<\/html>\s*\S+/i.test(html)) {
        issues.push({
            rule: 'malformed_html_structure',
            penalty: 20,
            message: 'Der Abmelde-Link oder Text steht außerhalb des </html>-Tags. Platziere den {{abmelde_link}} innerhalb des <body>.',
        });
        score += 20;
    }
    // 11. Fehlende Spintax-Variationsbreite für Razor2-Bypass
    const hasSpintax = /\{[^{}]+\|[^{}]+\}/.test(html);
    if (!hasSpintax && plainText.length > 200) {
        issues.push({
            rule: 'no_spintax_variation',
            penalty: 15,
            message: 'Der Text hat keine Spintax-Variationen ({Hallo|Guten Tag}). Identische Texte lösen Razor2-Hash-Sperren aus.',
        });
        score += 15;
    }
    // 12. Typische Kaltakquise-Signalwörter (Google-Bewertungen / Reputationsmanagement)
    if (/(google-rezensionen|google-bewertungen|unrechtmä|umsatz schädigen)/i.test(lowerHtml) && !hasSpintax) {
        issues.push({
            rule: 'cold_outreach_keywords',
            penalty: 20,
            message: 'Typische Kaltakquise-Formulierungen ohne Spintax erkannt. Nutze Variationsbausteine, um Razor2-Filter zu umgehen.',
        });
        score += 20;
    }
    const finalScore = Math.min(100, score);
    let rating = 'ausgezeichnet';
    if (finalScore >= 60)
        rating = 'kritisch';
    else if (finalScore >= 35)
        rating = 'mittel';
    else if (finalScore >= 15)
        rating = 'gut';
    return {
        score: finalScore,
        rating,
        issues,
    };
}
//# sourceMappingURL=spam-checker.js.map